import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ArticleContent } from '../components/ArticleContent';
import { careerArticles, practicalGuides } from '../data/articlesData';

const QuizComponent = () => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const questions = useMemo(() => [
    {
      id: 'q1',
      text: 'Face à une situation tendue, vous...',
      options: [
        { value: 1, label: 'A. Réagissez spontanément' },
        { value: 2, label: 'B. Respirez avant de répondre' },
        { value: 3, label: 'C. Analysez la situation' },
        { value: 4, label: 'D. Reformulez pour clarifier' }
      ]
    },
    {
      id: 'q2',
      text: 'Quand on vous explique un problème complexe, vous...',
      options: [
        { value: 1, label: 'A. Donnez une solution rapide' },
        { value: 2, label: 'B. Posez quelques questions' },
        { value: 3, label: 'C. Reformulez pour valider' },
        { value: 4, label: 'D. Proposez plusieurs pistes' }
      ]
    },
    {
      id: 'q3',
      text: 'En collaboration, vous êtes plutôt...',
      options: [
        { value: 1, label: 'A. Autonome' },
        { value: 2, label: 'B. Ouvert mais réservé' },
        { value: 3, label: 'C. Proactif' },
        { value: 4, label: 'D. Facilitateur' }
      ]
    },
    {
      id: 'q4',
      text: 'Quand vous recevez un feedback, vous...',
      options: [
        { value: 1, label: 'A. Vous braquez' },
        { value: 2, label: 'B. L\'écoutez mais l\'appliquez peu' },
        { value: 3, label: 'C. L\'utilisez pour vous ajuster' },
        { value: 4, label: 'D. Le demandez régulièrement' }
      ]
    },
    {
      id: 'q5',
      text: 'Face à un changement imprévu, vous...',
      options: [
        { value: 1, label: 'A. Résistez' },
        { value: 2, label: 'B. Vous adaptez avec effort' },
        { value: 3, label: 'C. Ajustez vos priorités' },
        { value: 4, label: 'D. Transformez en opportunité' }
      ]
    }
  ], []);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setResult(null);
  };

  const calculateScore = () => {
    if (Object.keys(answers).length < questions.length) {
      setResult({ error: 'Veuillez répondre à toutes les questions.' });
      return;
    }

    const score = Object.values(answers).reduce((sum, val) => sum + val, 0);
    
    let message = '';
    let level = '';
    if (score <= 8) {
      level = 'Réactivité';
      message = 'Vous avez de bonnes intentions, mais vos réactions prennent parfois le dessus.';
    } else if (score <= 12) {
      level = 'Conscience émergente';
      message = 'Vous êtes en progression, il manque encore des outils.';
    } else if (score <= 16) {
      level = 'Maturité solide';
      message = 'Vous démontrez une bonne maîtrise des soft skills clés.';
    } else {
      level = 'Intelligence professionnelle avancée';
      message = 'Vous êtes un profil très recherché en 2026.';
    }

    setResult({ score, level, message });
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Quiz interactif : Évaluez vos soft skills en 2026</h3>
      <p className="text-gray-600 mb-8">Choisissez une réponse par question. Le score se calcule automatiquement.</p>

      <div className="space-y-8">
        {questions.map((question, idx) => (
          <div key={question.id} className="p-6 bg-gray-50 rounded-lg" data-testid={`quiz-question-${question.id}`}>
            <p className="font-semibold text-gray-900 mb-4">{idx + 1}. {question.text}</p>
            <div className="space-y-2">
              {question.options.map((option) => (
                <label key={`${question.id}-opt-${option.value}`} className="flex items-center space-x-3 p-3 rounded hover:bg-white cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={answers[question.id] === option.value}
                    onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
                    className="w-4 h-4 text-gold"
                  />
                  <span className="text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Button 
          onClick={calculateScore}
          data-testid="quiz-submit-btn"
          className="w-full bg-gold hover:bg-[#9a7109] text-white text-lg py-6"
        >
          Voir mon résultat
        </Button>
      </div>

      {result && (
        <div className="mt-8 p-6 bg-gradient-to-r from-gold/10 to-gold/5 rounded-lg border-l-4 border-gold" data-testid="quiz-result">
          {result.error ? (
            <p className="text-red-600 font-semibold">{result.error}</p>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-900 mb-2">Votre score : {result.score}/20</p>
              <p className="text-xl font-semibold text-gold mb-4">{result.level}</p>
              <p className="text-gray-700 leading-relaxed">{result.message}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ArticleCard = ({ article, isExpanded, onToggle, goldTitles = false }) => (
  <Card className="border-0 shadow-lg hover:shadow-xl transition-all">
    <CardContent className="p-8">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {article.title}
          </h3>
          {article.subtitle && (
            <p className="text-lg text-gold mb-4">{article.subtitle}</p>
          )}
          <p className="text-gray-600 leading-relaxed mb-4">
            {article.excerpt}
          </p>
        </div>
        <Button
          onClick={onToggle}
          variant="outline"
          data-testid={`toggle-article-${article.id}`}
          className="ml-4 border-gold text-gold hover:bg-gold hover:text-white"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Réduire
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-2" />
              Lire l'article
            </>
          )}
        </Button>
      </div>

      {isExpanded && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <ArticleContent
            content={article.fullContent || article.content}
            goldTitles={goldTitles}
          />
        </div>
      )}
    </CardContent>
  </Card>
);

const ResourcesPage = () => {
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [expandedCareerArticle, setExpandedCareerArticle] = useState(null);

  return (
    <div className="pt-16" data-testid="resources-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] overflow-hidden">

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            Ressources
          </h1>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed text-center mb-6">
            Chez Nexor Talents, nous croyons que le recrutement ne s'arrête pas à la signature d'un contrat.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed text-center">
            C'est un parcours, une évolution, une quête de sens. Cette page est dédiée à tous ceux qui veulent avancer avec clarté, confiance et ambition.
          </p>
        </div>
      </section>

      {/* Guides pratiques */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 text-center">
            Guides pratiques
          </h2>

          <div className="mt-12 space-y-6">
            {practicalGuides.map((guide) => (
              <ArticleCard
                key={guide.id}
                article={guide}
                isExpanded={expandedArticle === guide.id}
                onToggle={() => setExpandedArticle(expandedArticle === guide.id ? null : guide.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Conseils carrière */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 text-center">
            Conseils carrière
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Des articles approfondis pour vous aider à naviguer votre carrière avec clarté et confiance
          </p>

          <div className="space-y-6 max-w-5xl mx-auto">
            {careerArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                isExpanded={expandedCareerArticle === article.id}
                onToggle={() => setExpandedCareerArticle(expandedCareerArticle === article.id ? null : article.id)}
                goldTitles
              />
            ))}
          </div>
        </div>
      </section>

      {/* Quiz Interactif */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Évaluez vos soft skills
            </h2>
            <p className="text-xl text-gray-600">
              Quiz interactif d'auto-évaluation
            </p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <QuizComponent />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
            À venir
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Nous enrichissons régulièrement cette page avec de nouveaux contenus. Suivez-nous sur nos réseaux sociaux pour ne rien manquer.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
